import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Select, Store, Actions, ofActionCompleted, ofActionDispatched } from '@ngxs/store';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { Sort } from '@angular/material/sort';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { FieldDef } from 'app/core/models';
import { LayoutService } from 'app/layout/layout.service';
import { Project } from 'app/modules/projects/projects.types';
import { ReportLayout, ReportType } from 'app/modules/reports/reports.types';
import { FileFormat, RelatedToType } from 'app/shared/enums';
import { getAllowedFields } from 'app/shared/functions';
import {
  GraphqlConnection,
  GraphqlConnectionMap,
  GraphqlFilterQueryObject,
  GraphqlFilterSort,
  GraphqlResultPageInfo,
} from 'app/shared/models/graphql';
import { FJSON, isArraysEqual } from 'app/shared/Utils/common.utils';
import { noop, Observable, Subject } from 'rxjs';
import { debounceTime, filter, switchMap, take, tap } from 'rxjs/operators';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import {
  ListFilterItem,
  ReportFilterEventArgs,
  ReportGraphqlFilter,
  ReportLayoutChart,
  ReportLayoutDefinition,
  ReportLayoutSummaryFormula,
  SummarizeMethod,
  TimeSeriesCadenceOptions,
  TimeSeriesFilter,
} from '../../reporting.types';
import { fjsonToListFilterItem, getFilterRows, replaceSortField } from '../../reporting.functions';
import { Graphql, Report, SummaryFormula } from '../../state/actions';
import { ReportingState } from '../../state/state';
import { ChartSetupModalComponent } from './visualizations/chart-setup-modal/chart-setup-modal.component';
import { ConfirmDialogComponent } from 'app/shared/components/widgets/dialog/confirm/confirm.component';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { FieldDefsState } from 'app/state/fields/state';
import { AccountState } from 'app/state/account/state';
import { FeatureFlag, NewPermission } from 'app/core/enums';
import { ObjectsManagerActions } from 'app/modules/settings/objects-manager/state/actions';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import cloneDeep from 'lodash-es/cloneDeep';

const REMOVE_PARENTHESES_REGEX = /\(([^)]+)\)/g;

@Component({
  selector: 'app-builder',
  templateUrl: './builder.component.html',
  styleUrls: ['./builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportingBuilderComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  @ViewChild('layoutName', { static: true }) layoutNameElement: ElementRef;

  private stateSelectedLayout$: Observable<ReportLayout<ReportLayoutDefinition>> = this.store.select(
    ReportingState.getSelectedLayout,
  );
  graphqlResults$: Observable<{
    rows: Project[];
    pageInfo?: GraphqlResultPageInfo;
  }> = this.store.select(ReportingState.getGraphqlResults);

  customObjects: CustomObject[];
  selectedLayout$: Observable<ReportLayout<ReportLayoutDefinition>>;
  fields: FieldDef[];
  filterableFields: any[];
  options: any;
  columns: string[] = [];
  listFilterItems: ListFilterItem[] = [];
  timeSeriesFilter = {} as TimeSeriesFilter;
  sort: GraphqlFilterSort[] = [];
  layout: ReportLayout<ReportLayoutDefinition>;
  ui = {
    isEditingName: false,
    isProcessing: false,
    isLoadingData: false,
    isAddingVisualization: false,
    isLeftPanelHidden: false,
    isTimeSeries: false,
  };
  leftTabGroupIndex = 0;
  dealTypeFields: FieldDef[];
  isCustomObject = false;
  hasNestedFiltersOn = false;
  hasSummaryFormulasOn = false;
  pageSize = 25;
  relatedToLabel = '';
  fieldsFilterFn = undefined;
  canShowGroupOptions = false;
  protected readonly NewPermission = NewPermission;

  get timeSeriesFilterIsValid(): boolean {
    if (!this.ui.isTimeSeries) {
      return false;
    }

    return !!this.timeSeriesFilter.startDate && !!this.timeSeriesFilter.endDate;
  }

  private filterDebouncer = new Subject<ReportFilterEventArgs>();
  private fieldsDebouncer = new Subject<string[]>();

  private get relatedToType(): string {
    return this.layout?.definition?.related_to_type;
  }

  private get connectionName(): string {
    if (this.layout?.report_type === ReportType.TimeSeries) {
      return GraphqlConnection.timeSeries;
    }

    if (this.layout?.definition.related_to_type === RelatedToType.Property) {
      return this.hasNestedFiltersOn ? GraphqlConnection.propertiesSqlBased : GraphqlConnection.properties;
    }

    if (this.layout?.definition.related_to_type === RelatedToType.Project) {
      return this.hasNestedFiltersOn ? GraphqlConnection.projectsSqlBased : GraphqlConnection.projects;
    }

    return GraphqlConnectionMap[this.relatedToType] ?? this.relatedToType;
  }

  private get keepConnectionCase(): boolean {
    return !!GraphqlConnectionMap[this.relatedToType];
  }

  private get currentLayoutModel(): ReportLayout<ReportLayoutDefinition> {
    return {
      ...this.layout,
      definition: { ...this.layout.definition, columns: this.columns },
      filters: JSON.stringify({
        fjson: this.getFiltersAsFJSON(),
        sort: this.sort,
      }),
    } as ReportLayout<ReportLayoutDefinition>;
  }

  constructor(
    private readonly activateRoute: ActivatedRoute,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly layoutService: LayoutService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly snackBar: MatSnackBar,
    private readonly matDialog: MatDialog,
    private readonly router: Router,
  ) {
    super();

    this.hasNestedFiltersOn = this.store
      .selectSnapshot(AccountState.getAccount)
      ?.features.includes(FeatureFlag.NESTED_FILTERS);

    this.hasSummaryFormulasOn = this.store
      .selectSnapshot(AccountState.getAccount)
      ?.features.includes(FeatureFlag.SUMMARY_FORMULAS);
  }

  ngOnInit(): void {
    this.layoutService.emitCommon('openCreateVisualization', null, 'subject');
    this.layoutService.emitCommon('openEditVisualization', null, 'subject');
    this.layoutService.emitCommon('openFiltersTab', null, 'subject');

    this.options = this.activateRoute.snapshot.data.options;
    this.dealTypeFields = getAllowedFields(
      this.store.selectSnapshot(FieldDefsState.getFieldDefs),
      RelatedToType.Project,
    );

    this.store.dispatch(new ObjectsManagerActions.Get());
    this.customObjects = this.store.selectSnapshot(ObjectsManagerState.get);

    if (this.hasSummaryFormulasOn) {
      this.store.dispatch(new SummaryFormula.Get());
    }

    this.activateRoute.params
      .pipe(
        untilComponentDestroyed(this),
        tap((params) => this.store.dispatch(new Report.GetById(params.id))),
      )
      .subscribe(noop);

    this.selectedLayout$ = this.stateSelectedLayout$.pipe(
      untilComponentDestroyed(this),
      tap((layout) => {
        this.layout = layout;
        const isTimeSeries = this.layout?.report_type === ReportType.TimeSeries;
        this.ui = { ...this.ui, isTimeSeries };
        if (isTimeSeries && !this.layout.definition.summarizeMethod && (this.layout.definition.summarizeMethod as string) !== '') {
          this.layout.definition.summarizeMethod = SummarizeMethod.SUM;
        }

        if (layout?.definition?.related_to_type) {
          this.isCustomObject = !!this.customObjects.find(({ name }) => name === this.relatedToType)?.id;
          this.canShowGroupOptions =
            isTimeSeries ||
            (this.hasSummaryFormulasOn &&
              (this.hasNestedFiltersOn || this.layout.definition.related_to_type === RelatedToType.Project));
          this.dealTypeFields = getAllowedFields(
            this.store.selectSnapshot(FieldDefsState.getFieldDefs),
            this.layout.definition.related_to_type,
          );
          const includeNestedFields = this.hasNestedFiltersOn;

          const fieldsSource = this.activateRoute.snapshot.data.fieldDefs;

          this.handleTrailTypeReport(fieldsSource);

          this.fields = getAllowedFields(fieldsSource, this.layout?.definition?.related_to_type, includeNestedFields);

          this.fields = this.fields.map((field) => ({
            ...field,
            group: this.ui.isTimeSeries && field.usedForTimeSeries ? 'Time Series' : this.getFieldGroup(field),
          }));

          this.filterableFields = getFilterRows({
            options: this.options,
            fields: this.fields,
          });
        }

        this.columns = layout?.definition?.columns;
        if (!this.hasNestedFiltersOn && this.columns) {
          this.columns = this.columns.filter((columns) => !columns.includes('.'));
        }
        this.timeSeriesFilter = (layout?.filters as unknown as ReportGraphqlFilter)?.timeSeries;

        if (this.canCallGraphQL()) {
          if (layout.filters) {
            this.listFilterItems = fjsonToListFilterItem({
              queryFilters: layout.filters,
              options: this.options,
              fields: this.ui.isTimeSeries ? this.dealTypeFields : [...this.fields],
            });
          }

          this.ui = { ...this.ui, isLoadingData: true };

          this.dispatchGraphQL(
            new Graphql.Action({
              connection: this.connectionName,
              fieldDefs: this.columnsToFieldDefs(),
              fields: layout.definition.columns,
              filter: {
                ...(layout.filters as unknown as ReportGraphqlFilter),
                pagination: {
                  page: 1,
                  size: this.pageSize,
                },
                queryObject: { report_layout_id: layout.id },
              },
              options: {
                keepConnectionCase: this.keepConnectionCase,
              },
              startDate: this.timeSeriesFilter?.startDate ?? null,
              endDate: this.timeSeriesFilter?.endDate ?? null,
              cadence: layout.definition.cadence,
              groupBy: layout.definition.groupBy,
              summarizeMethod: this.getSummarizeMethodIfAllowed(layout),
              relatedToType: this.layout.definition.related_to_type,
            }),
          );
        } else {
          this.ui = { ...this.ui, isLoadingData: false };
        }
      }),
    );

    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionCompleted(Graphql.Action),
        tap(() => {
          this.ui = { ...this.ui, isProcessing: false, isLoadingData: false };
          this.changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionDispatched(Graphql.Action),
        tap(() => {
          this.ui = { ...this.ui, isProcessing: true, isLoadingData: true };
          this.changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionCompleted(Report.Duplicate),
        switchMap(() => this.selectedLayout$.pipe(take(1))),
        tap((newSelectedLayout) => {
          this.snackBar.open('Report has been duplicated.', '', {
            duration: 3000,
          });
          this.router.navigate(['reporting/builder/', newSelectedLayout.id]);
        }),
      )
      .subscribe(noop);

    this.layoutService
      .getCommon('openCreateVisualization')
      .pipe(
        untilComponentDestroyed(this),
        tap(() => this.openCreateOrEditVisualization()),
      )
      .subscribe(noop);

    this.layoutService
      .getCommon('openEditVisualization')
      .pipe(
        untilComponentDestroyed(this),
        tap(() => this.openCreateOrEditVisualization(true)),
      )
      .subscribe(noop);

    this.layoutService
      .getCommon('openFiltersTab')
      .pipe(
        untilComponentDestroyed(this),
        tap(() => (this.leftTabGroupIndex = 1)),
      )
      .subscribe(noop);

    this.setupDebouncer(this.filterDebouncer);
    this.setupDebouncer(this.fieldsDebouncer, false, 500);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    this.store.dispatch(new Report.ClearSelected());
  }

  getFieldGroup(field): string {
    if (field.type === 'record') {
      return field.name.split('.')[0];
    } else {
      return field.related_to_type;
    }
  }

  onFieldsChanged({ fields, forceUpdate }: { fields: string[]; forceUpdate?: boolean }): void {
    if (fields && (!isArraysEqual([...fields], [...this.columns]) || forceUpdate)) {
      this.columns = fields;
      if (!this.hasNestedFiltersOn && this.columns) {
        this.columns = this.columns.filter((columns) => !columns.includes('.'));
      }
      this.ui = { ...this.ui, isLoadingData: true };
      this.fieldsDebouncer.next(fields);
    }
  }

  onFiltersChanged(filters: ListFilterItem[]): void {
    this.listFilterItems = filters;
    this.ui = { ...this.ui, isLoadingData: true };
    this.filterDebouncer.next({ listFilterItems: filters, timeSeriesFilter: this.timeSeriesFilter });
  }

  onTimeSeriesFiltersChanged(filters: TimeSeriesFilter): void {
    this.timeSeriesFilter = filters;
  }

  onPageChanged(pageEvent: PageEvent): void {
    const filters = this.layout.filters as unknown as ReportGraphqlFilter;

    this.ui = { ...this.ui, isLoadingData: true };

    this.pageSize = pageEvent.pageSize;

    this.dispatchGraphQL(
      new Graphql.Action({
        connection: this.connectionName,
        fieldDefs: this.columnsToFieldDefs(),
        fields: this.columns,
        filter: {
          ...filters,
          pagination: {
            page: pageEvent.pageIndex + 1,
            size: pageEvent.pageSize,
          },
          queryObject: { report_layout_id: this.layout.id },
        },
        options: {
          keepConnectionCase: this.keepConnectionCase,
        },
        startDate: this.timeSeriesFilter?.startDate ?? null,
        endDate: this.timeSeriesFilter?.endDate ?? null,
        cadence: this.layout.definition.cadence,
        groupBy: this.layout.definition.groupBy,
        summarizeMethod: this.getSummarizeMethodIfAllowed(this.layout),
        relatedToType: this.layout.definition.related_to_type,
      }),
    );
  }

  onSortChanged(sortEvent: Sort): void {
    const field = replaceSortField(sortEvent.active);

    this.ui = { ...this.ui, isLoadingData: true };
    this.sort = sortEvent.direction === '' ? null : [{ field, order: sortEvent.direction }];

    this.dispatchSave(false);
  }

  save(): void {
    this.ui = { ...this.ui, isProcessing: true, isLoadingData: true };

    this.dispatchSave();

    this.snackBar.open('Report has been saved.', '', {
      duration: 3000,
    });
  }

  refresh(): void {
    this.ui = { ...this.ui, isProcessing: true, isLoadingData: true };

    this.store.dispatch(new Report.GetById(this.layout.id, true));

    this.snackBar.open('Report has been refreshed.', '', {
      duration: 3000,
    });
  }

  export(fileFormat: string = FileFormat.CSV, excludeSubtotals = false): void {
    this.ui = { ...this.ui, isProcessing: true };
    const addReportLayoutId = (): { reportLayoutId: string } | {} => {
      const eligibleTypes = [
        RelatedToType.Contact,
        RelatedToType.Company,
        RelatedToType.Engagement,
        RelatedToType.Property,
        RelatedToType.Task,
      ];

      const relatedToType = this.layout.definition.related_to_type;

      if (eligibleTypes.includes(relatedToType) || !RelatedToType[relatedToType]) {
        return { reportLayoutId: this.layout.id.toString() };
      }

      return {};
    };

    const queryObject: GraphqlFilterQueryObject = {
      force_include_child: true,
      report_layout_id: this.layout.id,
    };

    if (this.ui.isTimeSeries) {
      queryObject.cadence = this.layout.definition.cadence;
    }

    this.dispatchGraphQL(
      new Graphql.Action({
        connection: this.connectionName,
        fieldDefs: this.columnsToFieldDefs(),
        fields: this.columns,
        filter: {
          ...(this.layout.filters as unknown as ReportGraphqlFilter),
          queryObject,
        },
        exportOptions: {
          format: fileFormat as FileFormat,
          fileName: `${this.layout.name}${Date.now()}`,
          ...addReportLayoutId(),
        },
        options: {
          keepConnectionCase: this.keepConnectionCase,
        },
        startDate: this.timeSeriesFilter?.startDate ?? null,
        endDate: this.timeSeriesFilter?.endDate ?? null,
        cadence: this.layout.definition.cadence,
        groupBy: this.layout.definition.groupBy,
        summarizeMethod: this.getSummarizeMethodIfAllowed(this.layout),
        excludeSubtotals,
        relatedToType: this.layout.definition.related_to_type,
      }),
    );
  }

  duplicateReport(): void {
    const model = this.currentLayoutModel;

    this.matDialog
      .open(ConfirmDialogComponent, {
        autoFocus: false,
        data: {
          title: 'Confirmation',
          message: `Are you sure you want to duplicate report "${model.name}"?`,
        },
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        filter((result: boolean) => result),
        tap(() => this.store.dispatch(new Report.Duplicate(model))),
      )
      .subscribe(noop);
  }

  layoutNameBlur({ target }: FocusEvent & { target: { innerText: string } }): void {
    if (target.innerText?.trim()) {
      this.layout.name = target.innerText;
    } else {
      target.innerText = this.layout.name;
    }
  }

  openCreateOrEditVisualization(edit = false): void {
    const fields = [
      ...(this.ui.isTimeSeries
        ? getAllowedFields(this.store.selectSnapshot(FieldDefsState.getFieldDefs), RelatedToType.Project)
        : this.fields.filter(({ name }) => this.filterableFields.map(({ id }) => id).includes(name))
      ).filter((x) => !x.usedForTimeSeries),
    ];

    this.matDialog
      .open(ChartSetupModalComponent, {
        autoFocus: false,
        width: '500px',
        data: {
          fields,
          title: this.layout.name,
          visualization: this.layout.definition.visualizations.length ? this.layout.definition.visualizations[0] : null,
          isTimeSeries: this.ui.isTimeSeries,
          allowRelatedObjectFields: false,
        },
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        filter((chart) => chart),
        tap((chart: Partial<ReportLayoutChart>) => {
          chart.title = this.layout.name;
          if (edit) {
            this.updateVisualizations([chart]);
          } else {
            this.updateVisualizations([...this.layout.definition.visualizations, chart]);
          }
        }),
      )
      .subscribe(noop);
  }

  onRemoveChart(chart: Partial<ReportLayoutChart>): void {
    this.updateVisualizations(this.layout.definition.visualizations.filter(({ id }) => chart.id !== id));
  }

  onRefreshChart(): void {
    this.updateVisualizations(this.layout.definition.visualizations, true);
  }

  toggleLeftPanel(): void {
    this.ui = { ...this.ui, isLeftPanelHidden: !this.ui.isLeftPanelHidden };
  }

  onCadenceChanged(cadence: TimeSeriesCadenceOptions): void {
    this.ui = { ...this.ui, isLoadingData: true };
    this.layout.definition.cadence = cadence;
    this.dispatchSave(false);
  }

  onSummarizeMethodChanged(summaryType: SummarizeMethod): void {
    this.ui = { ...this.ui, isLoadingData: true };
    this.layout.definition.summarizeMethod = summaryType;
    this.dispatchSave(false);
  }

  onGroupByFieldsChanged(fields: string[]): void {
    this.ui = { ...this.ui, isLoadingData: true };
    this.layout.definition.groupBy = fields.map((field) => ({ field, order: 'asc' }));
    if (this.ui.isTimeSeries && this.layout.definition.groupBy?.length) {
      this.columns = this.columns.filter((column) => column !== 'title');
    }
    this.dispatchSave(false);
  }

  filterNotTimeSeries(source: FieldDef[], filter: any): FieldDef[] {
    const timeSeriesFields = source
      .filter((x) => x.usedForTimeSeries)
      .map((x) => x.name.replace(REMOVE_PARENTHESES_REGEX, ''));
    return source.filter((x) => !x.usedForTimeSeries && !timeSeriesFields.includes(x.name));
  }

  onColumnsChanged(event: CdkDragDrop<string>): void {
    if (this.ui.isTimeSeries) {
      const tsFieldFilterArg = '(cf_name:';
      const tsFieldsCount = this.columns.filter((x) => x.indexOf(tsFieldFilterArg) > -1).length;
      const isGrouped = this.layout.definition.groupBy?.length > 0;
      if (isGrouped) {
        moveItemInArray(
          this.columns,
          event.previousIndex - this.layout.definition.groupBy?.length + tsFieldsCount,
          event.currentIndex - this.layout.definition.groupBy?.length + tsFieldsCount,
        );
      } else {
        moveItemInArray(this.columns, event.previousIndex + tsFieldsCount, event.currentIndex + tsFieldsCount);
      }
    } else if (this.layout.definition.groupBy?.length) {
      moveItemInArray(
        this.columns,
        event.previousIndex - this.layout.definition.groupBy?.length,
        event.currentIndex - this.layout.definition.groupBy?.length,
      );
    } else {
      moveItemInArray(this.columns, event.previousIndex, event.currentIndex);
      this.columns = [...this.columns];
      if (!this.hasNestedFiltersOn && this.columns) {
        this.columns = this.columns.filter((columns) => !columns.includes('.'));
      }
    }
    this.dispatchSave();
  }

  columnsToFieldDefs(): FieldDef[] {
    return Array.from(
      this.columns.reduce((acc: Set<FieldDef>, column: string) => {
        const splitValues = column.split('.');
        if (splitValues.length > 1) {
          // when related column cf_favorite_custom_a.cf_name is present
          // we need to add the parent field cf_favorite_custom_a because the field.meta.record_type is there.
          acc.add(this.fields.find((field) => field.name === splitValues[0]));
        }
        acc.add(this.fields.find((field) => field.name === column));
        return acc;
      }, new Set()),
    );
  }

  onSetFormulaToField(event: ReportLayoutSummaryFormula): void {
    this.ui = { ...this.ui, isLoadingData: true };

    let fieldFormula = this.layout.report_layout_summary_formulas.find((x) => x.field === event.field);

    if (fieldFormula) {
      fieldFormula._destroy = true;

      if (fieldFormula.summary_formula_id !== event.summary_formula_id) {
        this.layout.report_layout_summary_formulas.push(event);
      }
    } else {
      this.layout.report_layout_summary_formulas.push(event);
    }

    this.dispatchSave(false);
  }

  configurationChanged(model: ReportLayout<ReportLayoutDefinition>): void {
    this.layout = model;
    this.dispatchSave(false, false, true);
  }

  private setupDebouncer<T>(subject: Subject<T>, buildChart = true, time = 1000): void {
    subject
      .asObservable()
      .pipe(untilComponentDestroyed(this), debounceTime(time))
      .subscribe(() => this.dispatchSave(buildChart));
  }

  private updateVisualizations(charts: Partial<ReportLayoutChart>[], refreshChart = false): void {
    if (!refreshChart) {
      this.layout.definition.visualizations = charts;
    }

    this.dispatchSave(true, refreshChart);
  }

  private getFiltersAsFJSON(): string {
    const filters = this.listFilterItems.map((field) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { id: name, filter: operator, value, alternate_value, type } = field;
      const result = {
        name: name.replace(REMOVE_PARENTHESES_REGEX, ''),
        operator,
        value: alternate_value ? [alternate_value] : value,
      };

      if (
        type === 'record' &&
        Array.isArray(result.value) &&
        result.value.length === 1 &&
        result.value[0].length !== 2
      ) {
        result.value = result.value[0][0];

        if (Array.isArray(result.value) && result.value.length > 1) {
          result.value = [result.value];
        }
      }

      return result;
    });

    return FJSON(filters);
  }

  private dispatchSave(dispatchBuildChart = true, refreshChart = false, silent = false): void {
    if (silent === false) {
      this.ui = { ...this.ui, isProcessing: true, isLoadingData: true };
    }

    const model: ReportLayout<ReportLayoutDefinition> = {
      ...this.layout,
      definition: { ...this.layout.definition, columns: this.columns },
      filters: JSON.stringify({
        fjson: this.getFiltersAsFJSON(),
        sort: this.sort,
        timeSeries: this.timeSeriesFilter,
      }),
    };

    this.store.dispatch(new Report.Update(model, { dispatchBuildChart, refreshChart, silent }));
  }

  private dispatchGraphQL(action: Graphql.Action): void {
    this.store.dispatch(action);
  }

  private canCallGraphQL(): boolean {
    const hasFieldsSelected = this.layout?.definition?.columns?.length > 0;

    if (this.ui.isTimeSeries) {
      return hasFieldsSelected && this.timeSeriesFilterIsValid;
    }

    return hasFieldsSelected;
  }

  private getSummarizeMethodIfAllowed(layout: ReportLayout<ReportLayoutDefinition>): SummarizeMethod | undefined {
    if (this.ui.isTimeSeries || layout.definition.groupBy?.length || this.canShowGroupOptions) {
      const summarizeMethodStr = layout.definition.summarizeMethod as string;
      if (!summarizeMethodStr || summarizeMethodStr === '') {
        return undefined;
      }
      const enumValue = SummarizeMethod[summarizeMethodStr.toUpperCase() as keyof typeof SummarizeMethod];
      return enumValue || undefined;
    }
  }

  private handleTrailTypeReport(fieldsSource: FieldDef[]): void {
    if (this.layout?.definition?.related_to_type === RelatedToType.Trail) {
      const { target_field, related_to_type } = this.layout.definition.trailInfo;
      const targetFieldDef = cloneDeep(
        fieldsSource.find((x) => x.name === target_field && x.related_to_type === related_to_type),
      );

      if (targetFieldDef.type === 'select') {
        targetFieldDef.meta.options = this.options[targetFieldDef.meta.option_name].map((option) => ({
          ...option,
          id: String(option.id),
        }));
      }

      const fieldsShapeShiftFn = (field: FieldDef): void => {
        switch (field.name) {
          case 'created_at':
            field.label = 'Update Date';
            break;
          case 'old_val':
            field.label = `Old ${targetFieldDef.label}`;
            field.meta.shapeShifter = targetFieldDef;
            break;
          case 'old_value_duration':
            field.label = `Old ${targetFieldDef.label} Duration`;
            break;
          case 'new_val':
            field.label = `New ${targetFieldDef.label}`;
            field.meta.shapeShifter = targetFieldDef;
            break;
          case 'related_to':
            this.relatedToLabel = field.label;
            break;
        }
      };

      fieldsSource
        .filter((field) => field.related_to_type === RelatedToType.Trail)
        .forEach((field) => fieldsShapeShiftFn(field));

      this.fieldsFilterFn = (fieldDefs: FieldDef[]) => {
        const stg = fieldDefs.filter(
          (field) => field.meta?.reporting?.hidden === undefined || field.meta?.reporting?.hidden === false,
        );

        stg
          .filter((field) => field.related_to_type === RelatedToType.Trail)
          .forEach((field) => fieldsShapeShiftFn(field));

        return stg;
      };
    }
  }
}
